import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { navItems } from '../../data/site';
import logoLight from '../../assets/brand/logo-light.png';
import logoDark from '../../assets/brand/logo-dark.png';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Over the hero the bar is transparent, so the light logo is the readable
  // one; once it lands on the paper bar the dark logo takes over. Each mark
  // is used on the ground it was drawn for.
  const onLight = scrolled;

  const isCurrent = (item) =>
    item.dropdown
      ? item.dropdown.some((s) => s.path === location.pathname) || location.pathname.startsWith('/farm-tech')
      : location.pathname === item.path;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        onLight ? 'bg-paper/95 backdrop-blur shadow-[0_1px_0_rgba(20,36,27,0.10)]' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-[74px]">
          <Link to="/" className="flex items-center shrink-0" aria-label="Annpriya - home">
            <img
              src={onLight ? logoDark : logoLight}
              alt="Annpriya"
              className="h-8 w-auto block"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const current = isCurrent(item);
              const base = `relative px-3 py-2 text-[14px] font-medium rounded-md transition-colors whitespace-nowrap ${
                onLight
                  ? current
                    ? 'text-forest'
                    : 'text-soil/75 hover:text-forest'
                  : current
                  ? 'text-lime'
                  : 'text-paper/80 hover:text-lime'
              }`;
              return item.dropdown ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link to={item.path} className={`${base} inline-flex items-center gap-1.5`}>
                    {item.name}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${
                        openDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                    {current && <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-current" />}
                  </Link>
                  {openDropdown === item.name && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="w-[19rem] rounded-xl bg-ink ring-1 ring-white/10 shadow-2xl p-2">
                        {item.dropdown.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            className={`block rounded-lg px-3 py-2.5 transition-colors ${
                              location.pathname === sub.path
                                ? 'bg-lime/10'
                                : 'hover:bg-white/[0.06]'
                            }`}
                          >
                            <span className="block text-[13.5px] font-medium text-paper">{sub.name}</span>
                            <span className="block text-[12px] text-paper/45 mt-0.5">{sub.blurb}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.name} to={item.path} className={base}>
                  {item.name}
                  {current && <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-current" />}
                </Link>
              );
            })}
          </nav>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              onLight ? 'text-soil hover:text-forest' : 'text-paper hover:text-lime'
            }`}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu - always a light panel, so it uses the dark mark */}
      {mobileOpen && (
        <div className="lg:hidden bg-paper border-t border-soil/10 max-h-[calc(100vh-74px)] overflow-y-auto">
          <nav className="container mx-auto px-5 py-4 flex flex-col">
            {navItems.map((item) => (
              <div key={item.name} className="py-1">
                <Link
                  to={item.path}
                  className={`block py-2 font-medium ${
                    isCurrent(item) ? 'text-forest' : 'text-soil'
                  }`}
                >
                  {item.name}
                </Link>
                {item.dropdown && (
                  <div className="pl-4 border-l border-soil/15 ml-1 mt-1 space-y-1">
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.path}
                        className={`block py-1.5 text-[14px] ${
                          location.pathname === sub.path ? 'text-forest font-medium' : 'text-soil/70'
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
